import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Phone, Loader2 } from "lucide-react";
import { useOrg } from "@/contexts/OrgContext";
import { toast } from "sonner";
import { useCallbackServices, useSubmitCallbackRequest } from "@/hooks/useCallbackServices";

interface CallbackDialogProps {
  trigger?: React.ReactNode;
  variant?: "default" | "cta" | "booking";
}

const preferredTimes = [
  "Morning (9am - 12pm)",
  "Afternoon (12pm - 5pm)",
  "Evening (5pm - 8pm)",
  "Any time",
];

export function CallbackDialog({ trigger, variant = "default" }: CallbackDialogProps) {
  const { orgId } = useOrg();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    preferredTime: "",
    serviceId: "",
    bedrooms: "",
    bathrooms: "",
    notes: "",
  });

  const { data: services = [] } = useCallbackServices(orgId, open && variant === "booking");
  const submitCallbackMutation = useSubmitCallbackRequest();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.preferredTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Build notes with property details if provided
    let notesContent = formData.notes || "";
    if (variant === "booking") {
      const propertyDetails = [];
      if (formData.serviceId) {
        const selectedService = services.find(s => s.id === formData.serviceId);
        if (selectedService) propertyDetails.push(`Service: ${selectedService.name}`);
      }
      if (formData.bedrooms) propertyDetails.push(`Bedrooms: ${formData.bedrooms}`);
      if (formData.bathrooms) propertyDetails.push(`Bathrooms: ${formData.bathrooms}`);
      if (propertyDetails.length > 0) {
        notesContent = propertyDetails.join(", ") + (notesContent ? `\n\n${notesContent}` : "");
      }
    }

    try {
      await submitCallbackMutation.mutateAsync({
        organization_id: orgId,
        name: formData.name,
        phone: formData.phone,
        preferred_time: formData.preferredTime,
        notes: notesContent || null,
      });

      toast.success("Callback requested! We'll call you soon.");
      setOpen(false);
      setFormData({ name: "", phone: "", preferredTime: "", serviceId: "", bedrooms: "", bathrooms: "", notes: "" });
    } catch (error) {
      console.error('Error submitting callback request:', error);
      toast.error("Failed to submit request. Please try again.");
    }
  };

  const defaultTrigger = variant === "cta" ? (
    <button className="flex items-center gap-2 text-primary-foreground hover:text-primary-foreground/80 transition-colors">
      <Phone className="w-5 h-5" />
      <span className="font-semibold">or request callback</span>
    </button>
  ) : variant === "booking" ? (
    <Button variant="outline" size="lg" className="rounded-xl">
      Request a callback
    </Button>
  ) : (
    <Button variant="outline" size="sm">
      <Phone className="w-4 h-4 mr-2" />
      Request callback
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request a callback</DialogTitle>
          <DialogDescription>
            Leave your details and we'll call you back at your preferred time.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="callback-name">Your name *</Label>
            <Input
              id="callback-name"
              placeholder="Enter your name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="callback-phone">Phone number *</Label>
            <Input
              id="callback-phone"
              type="tel"
              placeholder="07xxx xxxxxx"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              required
            />
          </div>

          {variant === "booking" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="callback-service">Service type</Label>
                <Select
                  value={formData.serviceId}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, serviceId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bedrooms</Label>
                  <Select
                    value={formData.bedrooms}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, bedrooms: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, "6+"].map((num) => (
                        <SelectItem key={num} value={String(num)}>
                          {num}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bathrooms</Label>
                  <Select
                    value={formData.bathrooms}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, bathrooms: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, "5+"].map((num) => (
                        <SelectItem key={num} value={String(num)}>
                          {num}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="callback-preferredTime">Preferred call time *</Label>
            <Select
              value={formData.preferredTime}
              onValueChange={(value) => setFormData(prev => ({ ...prev, preferredTime: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a time" />
              </SelectTrigger>
              <SelectContent>
                {preferredTimes.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="callback-notes">Additional notes (optional)</Label>
            <Textarea
              id="callback-notes"
              placeholder="Let us know what you'd like to discuss..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
          
          <Button type="submit" className="w-full" disabled={submitCallbackMutation.isPending}>
            {submitCallbackMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Phone className="w-4 h-4 mr-2" />
                Request callback
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
